import { UserKeyPendingOrExpiredError } from '../errors/user-key-pending-or-expired-error';
import { addDays, areEqualUint8Array, arrayBufferToBase64, base64ToArrayBuffer, base64ToByteArray, getUnsafeDateNow } from '../utils/utils';
export class SavedKeyService {
    constructor(uiFrameAdapter, userKeysRepository, cryptoService, logger, localStorage) {
        this.uiFrameAdapter = uiFrameAdapter;
        this.userKeysRepository = userKeysRepository;
        this.cryptoService = cryptoService;
        this.logger = logger;
        this.localStorage = localStorage;
        this.signatureKeyDataLocalStorageKey = `ZeroTrust_SignatureKey_${this.uiFrameAdapter.companyId}_${this.uiFrameAdapter.userId}`;
    }
    async save(keyPair, askFrequency) {
        this.logger.logInformation('Saving key pair to local storage...');
        const [privateKeyArrayBuffer, publicKeyArrayBuffer] = await Promise.all([this.cryptoService.exportPrivateKey(keyPair.privateKey), this.cryptoService.exportPublicKey(keyPair.publicKey)]);
        this.localStorage.set(this.signatureKeyDataLocalStorageKey, {
            privateKey: arrayBufferToBase64(privateKeyArrayBuffer),
            publicKey: arrayBufferToBase64(publicKeyArrayBuffer),
            askFrequency,
            savedAt: getUnsafeDateNow(),
        });
        this.logger.logInformation(`Key pair saved to local storage. (askFrequency: ${askFrequency})`);
    }
    /** Loads and validates key from local storage. Returns undefined if no usable key present. */
    async loadUsable(vault) {
        const savedSignatureKeyData = this.localStorage.get(this.signatureKeyDataLocalStorageKey);
        if (!savedSignatureKeyData) {
            this.logger.logInformation('No saved key pair found.');
            return { keyPair: null, previousAskFrequency: 'perTask' };
        }
        const { savedAt, askFrequency } = savedSignatureKeyData;
        if (!askFrequency || askFrequency === 'perTask') {
            this.logger.logWarning('Saved key pair was set to be asked per task.');
            return { keyPair: null, previousAskFrequency: 'perTask' };
        }
        const now = getUnsafeDateNow();
        const expiration = addDays(new Date(savedAt), askFrequency === 'daily' ? 1 : 7);
        if (now > expiration) {
            this.logger.logInformation(`Saved key pair expired. (askFrequency: ${askFrequency}, savedAt: ${savedAt}, expiration: ${expiration.toISOString()}, now: ${now.toISOString()}})`);
            this.clear();
            return { keyPair: null, previousAskFrequency: askFrequency };
        }
        // try resolve key pair using saved public key
        const { privateKey, publicKey } = savedSignatureKeyData;
        this.logger.logInformation('Trying to validate saved key pair...');
        const validated = await this.validateSavedKeyPair(vault, new Uint8Array(base64ToArrayBuffer(publicKey)));
        if (!validated) {
            this.logger.logWarning(`Saved key was not found among user's private keys.`);
            this.clear();
            return { keyPair: null, previousAskFrequency: askFrequency };
        }
        const [importedPrivateKey, importedPublicKey] = await Promise.all([
            this.cryptoService.importPrivateKey(base64ToArrayBuffer(privateKey)),
            this.cryptoService.importPublicKey(base64ToArrayBuffer(publicKey))
        ]);
        this.logger.logInformation('Returning useable validated saved key pair.');
        return { keyPair: { privateKey: importedPrivateKey, publicKey: importedPublicKey }, previousAskFrequency: askFrequency };
    }
    clear() {
        this.logger.logInformation('Clearing saved key pair from local storage...');
        this.localStorage.remove(this.signatureKeyDataLocalStorageKey);
    }
    async validateSavedKeyPair(vault, publicKey) {
        // find saved public key in user's all keys
        const userKeys = await this.userKeysRepository.getPrivateKeysByUserId(this.uiFrameAdapter.companyId, this.uiFrameAdapter.userId);
        const userKey = userKeys.find(key => areEqualUint8Array(new Uint8Array(base64ToArrayBuffer(key.publicKey)), publicKey));
        if (!userKey) {
            return false;
        }
        try {
            this.validateUserKeyByVault(vault, userKey);
            return true;
        }
        catch (error) {
            if (error instanceof UserKeyPendingOrExpiredError) {
                this.logger.logWarning(error);
                return false;
            }
            throw error;
        }
    }
    validateUserKeyByVault(vault, userKey) {
        if (!vault.effectiveSignatureEntries.some(entry => areEqualUint8Array(entry.requestorPublicKey, base64ToByteArray(userKey.publicKey)))) {
            throw new UserKeyPendingOrExpiredError();
        }
    }
}
